import React, { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { CheckCircle, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trpcClient } from "@/lib/trpc";
import { mapProductionOrderListItem } from "@/lib/transformers/production-orders";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { PageFilterTags } from "@/components/shared/PageFilterTags";
import { type StatusSidebarItem } from "@/components/shared/StatusSidebar";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import { ResponsiveDataView } from "@/components/shared/ResponsiveDataView";
import type { FilterField } from "@/components/shared/FilterBar";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useRowSelection } from "@/hooks/useRowSelection";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { BulkActionBar, type BulkAction } from "@/components/shared/BulkActionBar";
import { exportToExcel, type ExcelColumnDef } from "@/lib/excel.utils";

const fmtDeadline = (order: any) => {
  const d = order.deadline || order.salesOrder?.deliveryDeadline || order.createdAt;
  if (!d) return "—";
  const dateObj = new Date(d);
  return isNaN(dateObj.getTime()) ? "—" : format(dateObj, "dd/MM/yyyy");
};

const MATERIAL_STATUS_LABEL: Record<string, string> = {
  PENDING: "Chờ xử lý",
  PROCESSING: "Đang chuẩn bị",
  READY: "Sẵn sàng",
};

const EXPORT_COLUMNS: ExcelColumnDef<any>[] = [
  { key: "lsxId", label: "Mã LSX", width: 18, exportFn: (o) => o.lsxId || o.orderId },
  { key: "productName", label: "Sản phẩm", width: 40 },
  { key: "customer", label: "Khách hàng", width: 30, exportFn: (o) => o.salesOrder?.customer?.name || o.customerId || "" },
  { key: "deadline", label: "Ngày yêu cầu", width: 14, exportFn: (o) => fmtDeadline(o) },
  { key: "materialStatus", label: "Vật tư", width: 16, exportFn: (o) => MATERIAL_STATUS_LABEL[o.materialStatus] || o.materialStatus || "" },
];

const STATUS_ITEMS: StatusSidebarItem[] = [
  { key: "PENDING", label: "Chờ xử lý", color: "bg-slate-100 text-slate-800", dotColor: "bg-slate-500" },
  { key: "PROCESSING", label: "Đang chuẩn bị", color: "bg-blue-100 text-blue-800", dotColor: "bg-blue-500" },
  { key: "READY", label: "Sẵn sàng", color: "bg-green-100 text-green-800", dotColor: "bg-green-500" },
];

const FILTER_FIELDS: FilterField[] = [
  { key: "orderId", label: "Mã LSX", type: "text", placeholder: "LSX-..." },
  {
    key: "productName",
    label: "Sản phẩm",
    type: "text",
    placeholder: "Tên SP...",
  },
];

export default function MaterialSupplyBoard({ hubTabs }: { hubTabs?: React.ReactNode } = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useRoleAccess();

  const {
    items: rawItems,
    paginatedItems: rawPaginated,
    isLoading,
    searchQuery,
    filterValues,
    showFilter,
    currentPage,
    pageSize,
    totalItems,
    statusCounts: serverStatusCounts,
    statusFilter,
    onStatusChange,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
  } = useServerListFilter<any>({
    queryKey: "material-supply-board",
    queryFn: (params) => trpcClient.productionOrders.list.query({
        ...params,
        // Always fetch active F0s, then filter materialStatus via statusFilter
        status: ["PLANNED", "READY_FOR_SCHEDULING", "IN_PROGRESS"],
        materialStatus: params.status, 
    }) as any,
  });

  const paginatedItems = useMemo(
    () => rawPaginated.map(mapProductionOrderListItem),
    [rawPaginated],
  );

  const filterTags = React.useMemo(() => {
    return STATUS_ITEMS.map((item) => {
      // Actually we don't have serverStatusCounts mapped to materialStatus right now,
      // so we just return the item with count 0.
      return {
        ...item,
        count: 0,
      };
    });
  }, []);

  const rowIds = useMemo(() => paginatedItems.map((o: any) => o.id as string), [paginatedItems]);
  const rowSelection = useRowSelection(rowIds);
  const { selectedIds, selectedCount, clearSelection } = rowSelection;
  const canUpdate = hasPermission("production", "update");

  const openDetail = (order: any) =>
    navigate({
      to: "/production-orders/$orderId",
      params: { orderId: order.orderId } as any,
    });

  const handleExport = (rows: any[]) =>
    exportToExcel(rows, EXPORT_COLUMNS, "cap_vat_tu_lsx", { sheetName: "Cấp vật tư" });

  const bulkActions: BulkAction[] = [
    {
      label: "Xuất Excel",
      icon: <Download className="h-3.5 w-3.5" />,
      onClick: () => handleExport(paginatedItems.filter((o: any) => selectedIds.has(o.id))),
    },
    ...(canUpdate
      ? [
          {
            label: "Đánh dấu đủ",
            icon: <CheckCircle className="h-3.5 w-3.5" />,
            onClick: async () => {
              const targets = paginatedItems.filter((o: any) => selectedIds.has(o.id) && o.materialStatus !== "READY");
              let ok = 0;
              for (const o of targets) {
                try {
                  await trpcClient.productionPlanning.updateMaterialReadiness.mutate({ id: o.id, materialStatus: "READY" as any });
                  ok++;
                } catch (error: any) {
                  toast.error(`${o.lsxId || o.orderId}: ${error.message || "Lỗi cập nhật"}`);
                }
              }
              if (ok) toast.success(`Đã cập nhật ${ok} LSX đủ vật tư`);
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["material-supply-board"] });
            },
          } as BulkAction,
        ]
      : []),
  ];

  const handleUpdateStatus = async (orderId: string, status: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await trpcClient.productionPlanning.updateMaterialReadiness.mutate({
        id: orderId,
        materialStatus: status as any,
      });
      toast.success("Cập nhật trạng thái vật tư thành công");
      queryClient.invalidateQueries({
        queryKey: ["material-supply-board"],
      });
    } catch (error: any) {
      toast.error(error.message || "Lỗi cập nhật trạng thái");
    }
  };

  return (
    <>
    <ListPageShell
      tabs={hubTabs}
      onExport={() => handleExport(paginatedItems)}
      headerFilters={
        <PageFilterTags
          items={filterTags}
          selectedKeys={
            statusFilter
              ? Array.isArray(statusFilter)
                ? statusFilter
                : [statusFilter]
              : []
          }
          onSelect={onStatusChange}
        />
      }
      unitLabel="LSX"
      availableViews={["table"]}
      activeView={"table"}
      onViewChange={() => {}}
      filterFields={FILTER_FIELDS}
      filterValues={filterValues}
      showFilter={showFilter}
      onToggleFilter={onToggleFilter}
      onClearAll={onClearAll}
      currentPage={currentPage}
      pageSize={pageSize}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
    >
      <ResponsiveDataView
        items={paginatedItems}
        emptyState={
          searchQuery
            ? "Không tìm thấy LSX phù hợp"
            : "Chưa có LSX nào đang chờ vật tư"
        }
        renderMobileCard={(order: any) => (
          <div
            className="flex flex-col bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden cursor-pointer"
            onClick={() => openDetail(order)}
          >
            <div className="flex items-center justify-between p-3 border-b border-slate-100 bg-slate-50/50">
              <span className="font-mono text-sm font-bold text-indigo-700">
                {order.lsxId || order.orderId}
              </span>
              <StatusBadge
                type="ReadinessStatus"
                value={order.materialStatus || "PENDING"}
              />
            </div>
            <div className="p-3">
              <div className="text-sm font-medium text-slate-800">
                {order.productName}
              </div>
              <div className="mt-2 text-xs text-slate-500 font-mono">
                Ngày yêu cầu: {(() => {
                  const d = order.deadline || order.salesOrder?.deliveryDeadline || order.createdAt;
                  if (!d) return "—";
                  const dateObj = new Date(d);
                  return isNaN(dateObj.getTime()) ? "—" : format(dateObj, "dd/MM/yyyy");
                })()}
              </div>
            </div>
          </div>
        )}
        >
          <div className="w-full overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-slate-500 bg-slate-50 border-b border-slate-200">
                <tr>
                  <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                  <th className="px-4 py-3 font-medium min-w-[120px]">
                    Mã LSX
                  </th>
                  <th className="px-4 py-3 font-medium min-w-[200px]">
                    Sản phẩm & Khách hàng
                  </th>
                  <th className="px-4 py-3 font-medium min-w-[120px]">
                    Ngày yêu cầu
                  </th>
                  <th className="px-4 py-3 font-medium min-w-[120px] text-center">
                    Vật tư
                  </th>
                  <th className="px-4 py-3 font-medium min-w-[150px] text-right">
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedItems.map((order: any) => (
                  <DataTableRow
                    key={order.id}
                    id={order.id}
                    rowSelection={rowSelection}
                    onClick={() => openDetail(order)}
                    className="hover:bg-slate-50/80 transition-colors"
                  >
                    <DataTableRowCheckbox id={order.id} rowSelection={rowSelection} />
                    <td className="px-4 py-3">
                      <span className="font-medium text-indigo-700">
                        {order.lsxId || order.orderId}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">
                        {order.productName}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 truncate max-w-[250px]">
                        {order.salesOrder?.customer?.name || order.customerId || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-600 font-mono text-xs">
                        {fmtDeadline(order)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge type="ReadinessStatus" value={order.materialStatus || "PENDING"} />
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        {order.materialStatus !== "READY" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-green-200 text-green-700 hover:bg-green-50"
                            onClick={(e) => handleUpdateStatus(order.id, "READY", e)}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" /> Đã đủ
                          </Button>
                        )}
                      </div>
                    </td>
                  </DataTableRow>
                ))}
              </tbody>
            </table>
          </div>
      </ResponsiveDataView>
    </ListPageShell>
    <BulkActionBar
      selectedCount={selectedCount}
      onClearSelection={clearSelection}
      actions={bulkActions}
    />
    </>
  );
}
